import React, { useState, useEffect } from 'react';
import Wrapper from '../../Main/Universal/Wrapper';
import { postAPI, postAPIForm } from '../../../app/api';
import editIcon from "../../../theme/assets/images/icon/edit-icon.svg"
import deleteIcon from "../../../theme/assets/images/icon/delete-icon.svg"
import fileIcon from "../../../theme/assets/images/icon/file-icon.png"
import openIcon from "../../../theme/assets/images/icon/open-icon.svg"
import deleteIcon02 from "../../../theme/assets/images/icon/delete-text-icon.svg"
import Swal from 'sweetalert2';


export default function Gallery() {
    // Folder states
    const [folders, setFolders] = useState([]);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [folderFiles, setFolderFiles] = useState([]);

    // UI states
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('folders'); // 'folders' or 'files'
    const [showFolderForm, setShowFolderForm] = useState(false);
    const [showFileForm, setShowFileForm] = useState(false);
    const [editingFileId, setEditingFileId] = useState(null);

    // Form states
    const [folderData, setFolderData] = useState({
        folder_name: '',
        status: 'active'
    });

    const [fileData, setFileData] = useState({
        gallery_file_name: '',
        file: null,
        status: 'active'
    });

    // Fetch folders on mount
    useEffect(() => {
        fetchFolders();
    }, []);

    // Fetch files when folder is selected
    useEffect(() => {
        if (selectedFolder) {
            fetchFolderFiles(selectedFolder.id);
        }
    }, [selectedFolder]);

    // Fetch all folders
    const fetchFolders = async () => {
        try {
            setLoading(true);
            const response = await postAPI('gallery/list', {});
            if (response?.status === 1) {
                setFolders(response?.data || []);
            }
        } catch (error) {
            console.error('Error fetching folders:', error);
            Swal.fire('Error', 'Failed to fetch gallery folders', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Fetch files in a folder
    const fetchFolderFiles = async (folderId) => {
        try {
            setLoading(true);
            const response = await postAPI('gallery/listFiles', { folder_id: folderId });
            if (response?.status === 1) {
                setFolderFiles(response?.data || []);
            }
        } catch (error) {
            console.error('Error fetching files:', error);
            Swal.fire('Error', 'Failed to fetch gallery files', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle folder form changes
    const handleFolderChange = (e) => {
        const { name, value } = e.target;
        setFolderData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle file form changes
    const handleFileChange = (e) => {
        const { name, value, files } = e.target;
        setFileData(prev => ({
            ...prev,
            [name]: files ? files[0] : value
        }));
    };

    // Create/Update folder
    const handleCreateFolder = async (e) => {
        e.preventDefault();
        try {
            if (!folderData.folder_name) {
                Swal.fire('Validation', 'Please enter folder name', 'warning');
                return;
            }

            const response = await postAPI('gallery/create', folderData);
            if (response?.status === 1) {
                Swal.fire('Success', 'Folder created successfully', 'success');
                resetFolderForm();
                fetchFolders();
            } else {
                Swal.fire('Error', response?.msg || 'Failed to create folder', 'error');
            }
        } catch (error) {
            console.error('Error creating folder:', error);
            Swal.fire('Error', error.message || 'Failed to create folder', 'error');
        }
    };

    // Upload file to folder
    const handleUploadFile = async (e) => {
        e.preventDefault();
        try {
            if (!selectedFolder) {
                Swal.fire('Validation', 'Please select a folder first', 'warning');
                return;
            }

            // If editing, only file name is required
            if (editingFileId) {
                if (!fileData.gallery_file_name) {
                    Swal.fire('Validation', 'Please enter file name', 'warning');
                    return;
                }

                const updateData = {
                    id: editingFileId,
                    gallery_file_name: fileData.gallery_file_name,
                    status: fileData.status === 'active' ? 1 : 0
                };

                // If new file is selected, upload it first
                if (fileData.file) {
                    const fileFormData = new FormData();
                    fileFormData.append('baseDir', 'gallery');
                    fileFormData.append('filename', 'gallery_file');
                    fileFormData.append('original_file', fileData.file);

                    const uploadResponse = await postAPIForm('file/uploadFile', fileFormData);
                    if (uploadResponse?.status === 1) {
                        updateData.file_url = uploadResponse?.filepath;
                    }
                }

                const response = await postAPI('gallery/updateFile', updateData);
                if (response?.status === 1) {
                    Swal.fire('Success', 'File updated successfully', 'success');
                    resetFileForm();
                    fetchFolderFiles(selectedFolder.id);
                } else {
                    Swal.fire('Error', response?.msg || 'Failed to update file', 'error');
                }
                return;
            }

            if (!fileData.gallery_file_name || !fileData.file) {
                Swal.fire('Validation', 'Please enter file name and select a file', 'warning');
                return;
            }

            // Upload file
            const fileFormData = new FormData();
            fileFormData.append('baseDir', 'gallery');
            fileFormData.append('filename', 'gallery_file');
            fileFormData.append('original_file', fileData.file);

            const uploadResponse = await postAPIForm('file/uploadFile', fileFormData);
            if (uploadResponse?.status !== 1) {
                Swal.fire('Error', 'Failed to upload file', 'error');
                return;
            }

            // Add file to gallery
            const submitData = {
                folder_id: selectedFolder.id,
                gallery_file_name: fileData.gallery_file_name,
                file_url: uploadResponse?.filepath,
                created_by: 'admin'
            };

            const response = await postAPI('gallery/uploadFile', submitData);
            if (response?.status === 1) {
                Swal.fire('Success', 'File uploaded successfully', 'success');
                resetFileForm();
                fetchFolderFiles(selectedFolder.id);
            } else {
                Swal.fire('Error', response?.msg || 'Failed to add file', 'error');
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            Swal.fire('Error', error.message || 'Failed to upload file', 'error');
        }
    };

    // Delete folder
    const handleDeleteFolder = async (id) => {
        const result = await Swal.fire({
            title: 'Confirm Delete',
            text: 'Are you sure you want to delete this folder and all its files?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('gallery/delete', { id });
                if (response?.status === 1) {
                    Swal.fire('Success', 'Folder deleted successfully', 'success');
                    if (selectedFolder?.id === id) {
                        setSelectedFolder(null);
                        setFolderFiles([]);
                    }
                    fetchFolders();
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to delete folder', 'error');
            }
        }
    };

    // Delete file
    const handleDeleteFile = async (id) => {
        const result = await Swal.fire({
            title: 'Confirm Delete',
            text: 'Are you sure you want to delete this file?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('gallery/deleteFile', { id });
                if (response?.status === 1) {
                    Swal.fire('Success', 'File deleted successfully', 'success');
                    if (selectedFolder) {
                        fetchFolderFiles(selectedFolder.id);
                    }
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to delete file', 'error');
            }
        }
    };

    const resetFolderForm = () => {
        setFolderData({ folder_name: '', status: 'active' });
        setShowFolderForm(false);
    };

    const resetFileForm = () => {
        setFileData({ gallery_file_name: '', file: null, status: 'active' });
        setShowFileForm(false);
        setEditingFileId(null);
    };

    const handleEditFile = (file) => {
        setFileData({
            gallery_file_name: file.gallery_file_name,
            file: null,
            status: file.status === 1 ? 'active' : 'inactive'
        });
        setEditingFileId(file.id);
        setShowFileForm(true);
    };

    // Toggle show/unshow for a file
    const handleToggleShowFile = async (file) => {
        try {
            setLoading(true);
            const newStatus = file.status === 1 ? 0 : 1;
            const response = await postAPI('gallery/updateFile', {
                id: file.id,
                status: newStatus
            });
            if (response?.status === 1) {
                Swal.fire('Success', `File ${newStatus === 1 ? 'shown' : 'hidden'} successfully`, 'success');
                if (selectedFolder) {
                    fetchFolderFiles(selectedFolder.id);
                }
            } else {
                Swal.fire('Error', response?.msg || 'Failed to update file status', 'error');
            }
        } catch (error) {
            console.error('Error toggling file status:', error);
            Swal.fire('Error', error.message || 'Failed to update file status', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Wrapper>
                <div className="container-fluid">
                    <div className="row mb-4">
                        <div className="col-12">
                            <h3 className="mb-0">Gallery Management</h3>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="row mb-4">
                        <div className="col-12 d-flex justify-content-between">
                            <ul className="pagination" role="tablist">
                                <li className="">
                                    <button className={`nav-link ${activeTab === 'folders' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('folders')}
                                    >Gallery Folders</button>
                                </li>
                                <li className="">
                                    <button className={`nav-link ${activeTab === 'files' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('files')}
                                        disabled={!selectedFolder}
                                    >Gallery Files {selectedFolder && `(${selectedFolder.folder_name})`}
                                    </button>
                                </li>
                            </ul>
                            {activeTab === 'folders' && (
                                // <button className="btn btn-primary" onClick={() => setShowFolderForm(!showFolderForm)}>
                                //     {showFolderForm ? 'Cancel' : 'Create Folder'}
                                // </button>
                                <button className="btn btn-primary" onClick={() => document.getElementById("openFolderModalButton").click()}>
                                    {showFolderForm ? 'Cancel' : 'Create Folder'}
                                </button>
                            )}
                            {activeTab === 'files' && selectedFolder && (
                                <button className="btn btn-primary" onClick={() => document.getElementById("openFileModalButton").click()}>
                                    {showFileForm ? 'Cancel' : 'Add File'}
                                </button>
                            )}
                        </div>
                    </div>
                    <button
                        id="openFolderModalButton"
                        type="button"
                        className="d-none"
                        data-bs-toggle="modal"
                        data-bs-target="#folderModal"
                    ></button>
                    {/* Folders Tab */}
                    {activeTab === 'folders' && (
                        <>
                            {/* {showFolderForm && (
                                <div className="card mb-4">
                                    <div className="card-body">
                                        <h5 className="card-title">Create New Gallery Folder</h5>
                                        <form onSubmit={handleCreateFolder}>
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Folder Name *</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="folder_name"
                                                        value={folderData.folder_name}
                                                        onChange={handleFolderChange}
                                                        placeholder="e.g., Alumni Photos 2024"
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Status</label>
                                                    <select
                                                        className="form-control"
                                                        name="status"
                                                        value={folderData.status}
                                                        onChange={handleFolderChange}
                                                    >
                                                        <option value="active">Active</option>
                                                        <option value="inactive">Inactive</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <button type="submit" className="btn btn-success">
                                                Create Folder
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            )} */}
                            <div className="modal fade modal-white" id="folderModal" tabIndex="-1" aria-hidden="true">
                                <div className="modal-dialog modal-md modal-dialog-centered">
                                    <div className="modal-content">

                                        <div className="modal-header">
                                            <h5 className="modal-title">Create New Gallery Folder</h5>
                                            <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                                        </div>

                                        <div className="modal-body">
                                            <form onSubmit={handleCreateFolder}>
                                                <div className="row">
                                                    <div className="col-md-12 mb-3">
                                                        <label className="form-label">Folder Name *</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="folder_name"
                                                            value={folderData.folder_name}
                                                            onChange={handleFolderChange}
                                                            required
                                                        />
                                                    </div>

                                                    <div className="col-md-12 mb-3">
                                                        <label className="form-label">Status</label>
                                                        <select
                                                            className="form-control"
                                                            name="status"
                                                            value={folderData.status}
                                                            onChange={handleFolderChange}
                                                        >
                                                            <option value="active">Active</option>
                                                            <option value="inactive">Inactive</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <button type="submit" className="btn btn-success">Create Folder</button>
                                            </form>
                                        </div>

                                    </div>
                                </div>
                            </div>

                            <div className="card">
                                <div className="card-body">
                                    {loading ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            {folders && folders.length > 0 ? (
                                                <div className="row">
                                                    {folders.map((folder) => (
                                                        <div key={folder.id} className="col-md-4 col-sm-6 mb-4">
                                                            <div className="card h-100 cursor-pointer"
                                                                style={{
                                                                    border: selectedFolder?.id === folder.id ? '3px solid #007bff' : '1px solid #ddd',
                                                                    cursor: 'pointer'
                                                                }}>
                                                                <div className="card-body text-center">
                                                                    <div className="mb-3 gallery-icon"><img src={fileIcon} /></div>
                                                                    <h5 className="card-title">{folder.folder_name}</h5>
                                                                    <p className="card-text text-muted small">
                                                                        Created: {new Date(folder.createdAt).toLocaleDateString()}
                                                                    </p>
                                                                    <span className={`badge badge-${folder.status === 1 ? 'success' : 'danger'}`}>
                                                                        {folder.status === 1 ? 'Active' : 'Inactive'}
                                                                    </span>
                                                                </div>
                                                                <div className="card-footer bg-light d-flex justify-content-center">
                                                                    {
                                                                        folder?.edit_permission ?
                                                                            <>
                                                                                <button
                                                                                    className="btn me-2"
                                                                                    onClick={() => {
                                                                                        setSelectedFolder(folder);
                                                                                        setActiveTab('files');
                                                                                    }}
                                                                                >
                                                                                    <img src={openIcon} />
                                                                                </button>
                                                                                <button
                                                                                    className="btn"
                                                                                    onClick={() => handleDeleteFolder(folder.id)}
                                                                                >
                                                                                    <img src={deleteIcon02} />
                                                                                </button>
                                                                            </>
                                                                            : ''
                                                                    }
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-5">
                                                    <p className="text-muted">No gallery folders found</p>
                                                    <p className="text-muted">Create a new folder to get started</p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                    
                    {/* Files Tab */}
                    {activeTab === 'files' && selectedFolder && (
                        <>
                            <div className="row mb-0">
                                <div className="col-12">
                                    <div className="alert alert-info py-0 my-0 ml-0">
                                        <strong>Folder:</strong> {selectedFolder.folder_name}
                                        <button
                                            className="btn btn-sm btn-secondary ms-2"
                                            onClick={() => setSelectedFolder(null)}
                                        >
                                            Change Folder
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <button
                                id="openFileModalButton"
                                type="button"
                                className="d-none"
                                data-bs-toggle="modal"
                                data-bs-target="#fileModal"
                            ></button>
                            <div className="row mb-4">
                                <div className="col-12">
                                    {/* <button
                                        className="btn btn-primary"
                                        onClick={() => setShowFileForm(!showFileForm)}
                                    >
                                        {showFileForm ? 'Cancel' : 'Add File'}
                                    </button> */}
                                    
                                </div>
                            </div>

                            {/* {showFileForm && (
                                <div className="card mb-4">
                                    <div className="card-body">
                                        <h5 className="card-title">{editingFileId ? 'Edit Gallery File' : 'Add File to Gallery'}</h5>
                                        <form onSubmit={handleUploadFile}>
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">File Name *</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="gallery_file_name"
                                                        value={fileData.gallery_file_name}
                                                        onChange={handleFileChange}
                                                        placeholder="e.g., Photo 1"
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Select File {editingFileId ? '(Optional - leave empty to keep existing)' : '*'}</label>
                                                    <input
                                                        type="file"
                                                        className="form-control"
                                                        name="file"
                                                        onChange={handleFileChange}
                                                        accept="image/*,video/*,.pdf"
                                                        required={!editingFileId}
                                                    />
                                                </div>
                                            </div>
                                            <button type="submit" className="btn btn-success">
                                                {editingFileId ? 'Update File' : 'Upload File'}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            )} */}
                            <div className="modal fade modal-white" id="fileModal" tabIndex="-1" aria-hidden="true">
                                <div className="modal-dialog modal-md modal-dialog-centered">
                                    <div className="modal-content">

                                        <div className="modal-header">
                                            <h5 className="modal-title">{editingFileId ? "Edit Gallery File" : "Add File to Gallery"}</h5>
                                            <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                                        </div>

                                        <div className="modal-body">
                                            <form onSubmit={handleUploadFile}>
                                                <div className="row">

                                                    <div className="col-md-12 mb-3">
                                                        <label className="form-label">File Name *</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="gallery_file_name"
                                                            value={fileData.gallery_file_name}
                                                            onChange={handleFileChange}
                                                            required
                                                        />
                                                    </div>

                                                    <div className="col-md-12 mb-3">
                                                        <label className="form-label">Select File {editingFileId ? "(Optional)" : "*"}</label>
                                                        <input
                                                            type="file"
                                                            className="form-control"
                                                            name="file"
                                                            onChange={handleFileChange}
                                                            accept="image/*,video/*,.pdf"
                                                            required={!editingFileId}
                                                        />
                                                    </div>

                                                </div>

                                                <button type="submit" className="btn btn-success">
                                                    {editingFileId ? "Update File" : "Upload File"}
                                                </button>
                                            </form>
                                        </div>

                                    </div>
                                </div>
                            </div>

                            <div className="card">
                                <div className="card-body">
                                    {loading ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            {folderFiles && folderFiles.length > 0 ? (
                                                <div className="table-responsive">
                                                    <table className="table table-striped table-hover cus-table">
                                                        <thead className="table-dark">
                                                            <tr>
                                                                <th>Image</th>
                                                                <th>File Name</th>
                                                                <th>Created Date</th>
                                                                <th>Status</th>
                                                                <th>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {folderFiles.map((file) => (
                                                                <tr key={file.id}>
                                                                    <td>
                                                                        {file.status === 1 ? (
                                                                            <img src={file.file_url} alt={file.gallery_file_name} style={{ width: '50px', height: '50px', objectFit: 'cover' }} />
                                                                        ) : (
                                                                            <span className="text-muted">Hidden</span>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        <i className="fas fa-file me-2"></i>
                                                                        {file.gallery_file_name}
                                                                    </td>
                                                                    <td>{new Date(file.createdAt).toLocaleDateString()}</td>
                                                                    <td>
                                                                        <span className={`badge badge-${file.status === 1 ? 'success' : 'danger'}`}>
                                                                            {file.status === 1 ? 'Active' : 'Inactive'}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        {/* Show/Unshow button */}
                                                                        <button
                                                                            className={`btn me-2 py-0 my-0 mx-0 ${file.status === 1 ? 'btn-warning' : 'btn-success'}`}
                                                                            onClick={() => handleToggleShowFile(file)}
                                                                        >
                                                                            {file.status === 1 ? 'Unshow' : 'Show'}
                                                                        </button>
                                                                        <button
                                                                            className="btn py-0 my-0 mx-0"
                                                                            onClick={() => handleDeleteFile(file.id)}
                                                                        >
                                                                            <img src={deleteIcon} />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <div className="text-center py-5">
                                                    <p className="text-muted">No files in this folder yet</p>
                                                    <p className="text-muted">Add files using the button above</p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </Wrapper>
        </>
    );
}
